"use client"

import {Button} from "@/components/ui/button";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {IconGoogle} from "@/app/components/icons/Icons";
import {Backdrop} from "@/app/components/onboarding/Backdrop";
import {z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {toast} from "sonner";
import {Form, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Loader} from "@/app/components/loader";
import {Check} from "lucide-react";
import {Toaster} from "@/components/ui/sonner"
import {usePathname} from "next/navigation";
import {EarlyAccessRequestForm} from "@/app/components/onboarding/ClosedAccess";
import {AskDotgridIcon} from "@/app/components/chat/AskDotgridIcon";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";
import {signIn as clientSignIn} from "next-auth/react";
import Link from "next/link";
import {magicLinkRequestAPI} from "@/app/apiPaths";
import {recaptchaVerificationAPI} from "@/app/apiPaths";
import {
  MagicLinkRequest,
  MagicLinkResponse,
  VerifyRecaptchaRequest,
} from "@/pb/api/devplan/services/web/auth";
import {
  RecaptchaChallenge,
  type RecaptchaChallengeHandle,
} from "@/app/components/onboarding/RecaptchaChallenge";
import {normalizeCallbackPath} from "@/app/auth/callbackUrl";

function getCallbackUrl(): string | undefined {
  if (typeof window === "undefined") return undefined
  return normalizeCallbackPath(new URLSearchParams(window.location.search).get("callbackUrl"))
}

export function SignIn({frontDoorClosed}: {frontDoorClosed: boolean}) {
  const pathname = usePathname()
  if (pathname === "/prelaunch" && frontDoorClosed) {
    return <PrelaunchFeedback/>
  }
  return <div className="flex h-full items-center justify-center w-full space-x-2 p-2">
    <Toaster/>
    <div className="relative z-10 flex h-full w-full flex-1 flex-col space-y-4 pb-[12vh]">
      <div className="flex-[0.8] flex justify-center items-end w-full">
        <div className="max-w-[350px] w-full space-y-4">
          <div className="w-full space-y-4">
            <div className="w-full">
              <SignInBrandIcon/>
              <h1 className="flex text-[30px] font-bold">
                {frontDoorClosed ? "Sign in if you already have an account" : "Sign in or create an account"}
              </h1>
              <p className="text-sm leading-6 text-muted-foreground">
                {frontDoorClosed
                  ? "Devplan is in pre-launch. Magic links and Google sign-in are available for existing workspace members and invited users."
                  : "Use Google or a magic link to continue to Devplan."}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 flex justify-center items-start w-full">
        <div className="max-w-[350px] w-full">
          <div className="w-full space-y-4">
            {frontDoorClosed ? (
              <Accordion type="single" defaultValue="signin" className="w-full">
                <AccordionItem value="signin" className="border-b-0">
                  <AccordionTrigger className="py-2 hover:no-underline">Sign in</AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <SignInActions/>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="access" className="mt-3 border-b-0">
                  <AccordionTrigger className="py-2 hover:no-underline">Request access</AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Leave your email and a note. We will follow up when we can open access.
                    </p>
                    <EarlyAccessRequestForm/>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <SignInActions/>
            )}
            <TermsOfUse/>
          </div>
        </div>
      </div>
    </div>
    <SignInBackdrop/>
  </div>
}

function PrelaunchFeedback() {
  const initialEmail = getPrelaunchEmailFromCookie()
  return <div className="flex h-full items-center justify-center w-full space-x-2 p-2">
    <Toaster/>
    <div className="relative z-10 flex h-full w-full flex-1 flex-col space-y-4">
      <div className="flex-1 flex justify-center items-end w-full">
        <div className="max-w-[350px] w-full space-y-3">
          <Link href="/" aria-label="Go to sign in" className="inline-flex">
            <SignInBrandIcon/>
          </Link>
          <h1 className="text-[30px] font-bold">Devplan is in pre-launch</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            We are not accepting new self-serve signups yet. Leave your email and a message, and we will follow up.
          </p>
        </div>
      </div>
      <div className="flex-1 flex justify-center items-start w-full">
        <div className="max-w-[350px] w-full">
          <EarlyAccessRequestForm initialEmail={initialEmail}/>
        </div>
      </div>
    </div>
    <SignInBackdrop/>
  </div>
}

function SignInBackdrop() {
  return <div className="hidden h-full w-full flex-1 md:flex">
    <Backdrop blendArtwork/>
  </div>
}

function getPrelaunchEmailFromCookie(): string {
  if (typeof document === "undefined") {
    return ""
  }
  const entry = document.cookie
    .split("; ")
    .find(cookie => cookie.startsWith("prelaunch_email="))
  if (!entry) {
    return ""
  }
  return decodeURIComponent(entry.substring("prelaunch_email=".length))
}

function SignInBrandIcon() {
  return <div className="mb-3 flex size-[38px] items-center justify-center text-foreground">
    <AskDotgridIcon size={26} auto/>
  </div>
}

function TermsOfUse() {
  return <div className="flex text-sm text-muted-foreground text-center w-full justify-center">
    <div className="max-w-[300px]">
      By signing in to Devplan, you agree to the <a
      href="https://www.devplan.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground"
    >Terms of Use</a> and <a
      href="https://www.devplan.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground"
    >Privacy Policy</a>.
    </div>
  </div>
}

function SignInActions() {
  const recaptchaRef = useRef<RecaptchaChallengeHandle>(null)
  const [recaptchaReady, setRecaptchaReady] = useState(false)

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get("recaptcha") !== "required") return
    recaptchaRef.current?.reset("Verification expired during sign-in. Complete reCAPTCHA and try again.")
    url.searchParams.delete("recaptcha")
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`)
  }, [])

  return <div className="space-y-4">
    <GoogleSigning recaptchaRef={recaptchaRef} recaptchaReady={recaptchaReady}/>
    <div className="max-w-[350px] w-full flex items-center space-x-1">
      <div className="grow border-t"></div>
      <div className="text-xs text-muted-foreground">OR</div>
      <div className="grow border-t"></div>
    </div>
    <EmailSigning recaptchaRef={recaptchaRef} recaptchaReady={recaptchaReady}/>
    <RecaptchaChallenge ref={recaptchaRef} onReadyChange={setRecaptchaReady}/>
  </div>
}

function GoogleSigning({
  recaptchaRef,
  recaptchaReady,
}: {
  recaptchaRef: React.RefObject<RecaptchaChallengeHandle | null>;
  recaptchaReady: boolean;
}) {
  const [processing, setProcessing] = useState(false)
  return <div>
    <Button type="button" variant="outline" className="w-full" disabled={processing || !recaptchaReady} onClick={async () => {
      setProcessing(true)
      const token = await recaptchaRef.current?.requireToken("google_sign_in")
      if (!token) {
        setProcessing(false)
        return
      }
      try {
        const response = await fetch(recaptchaVerificationAPI(), {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify(VerifyRecaptchaRequest.toJSON({token})),
        })
        if (!response.ok) {
          recaptchaRef.current?.reset(await verificationResponseError(response))
          return
        }
        recaptchaRef.current?.reset()
        const callbackUrl = getCallbackUrl()
        await clientSignIn("google", callbackUrl ? {redirectTo: callbackUrl} : undefined, {prompt: "select_account"})
      } catch {
        recaptchaRef.current?.reset("reCAPTCHA could not be verified. Please try again.")
      } finally {
        setProcessing(false)
      }
    }}>{processing || !recaptchaReady ? <Loader/> : <IconGoogle/>} Google</Button>
  </div>
}

const emailSigninSchema = z.object({
  email: z.string().trim().email({message: "Must be a valid email"}),
})

function EmailSigning({
  recaptchaRef,
  recaptchaReady,
}: {
  recaptchaRef: React.RefObject<RecaptchaChallengeHandle | null>;
  recaptchaReady: boolean;
}) {
  const [processing, setProcessing] = useState(false);
  const [added, setAdded] = useState(false);
  const form = useForm<z.infer<typeof emailSigninSchema>>({
    resolver: zodResolver(emailSigninSchema),
    defaultValues: {email: ""},
  });
  const onSubmit = useCallback(async (v: z.infer<typeof emailSigninSchema>) => {
    setProcessing(true);
    const recaptchaToken = await recaptchaRef.current?.requireToken("magic_link")
    if (!recaptchaToken) {
      setProcessing(false)
      return
    }
    try {
      const callbackUrl = getCallbackUrl()
      const resp = await fetch(magicLinkRequestAPI(), {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(MagicLinkRequest.toJSON({
          email: v.email,
          recaptchaToken,
          ...(callbackUrl ? {redirectTo: callbackUrl} : {}),
        })),
      })
      if (!resp.ok) {
        if (resp.status === 400 || resp.status === 503) {
          recaptchaRef.current?.reset(await verificationResponseError(resp))
        } else {
          recaptchaRef.current?.reset()
          toast.error(await responseError(resp, "We could not send a magic link. Please try again."))
        }
        return
      }
      recaptchaRef.current?.reset()
      const {callbackUrl: developmentCallbackUrl} = MagicLinkResponse.fromJSON(await resp.json())
      if (developmentCallbackUrl) {
        window.location.assign(developmentCallbackUrl)
        return
      }
      setAdded(true)
      toast.success("If you have an account, a sign-in email has been sent. Please check your email.")
    } catch {
      recaptchaRef.current?.reset("reCAPTCHA could not be verified. Please try again.")
      toast.error(`Sorry, something went wrong. Please, contact info@devplan.com to get support.`)
    } finally {
      setProcessing(false);
    }
  }, [recaptchaRef])
  return <Form {...form}>
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <FormField
        control={form.control}
        name="email"
        render={({field}) => (<FormItem>
          <FormLabel>Email</FormLabel>
          <Input type="email" {...field} placeholder="Email"/>
          <FormMessage/>
        </FormItem>)}
      />
      {!added && <Button className="w-full" type="submit" disabled={processing || !recaptchaReady}>
        {(processing || !recaptchaReady) && <Loader/>} Send magic link
      </Button>}
      {added && <div className="flex gap-2"><Check strokeWidth={3}/> If you have an account, a sign-in email has
          been sent. Please check your email.</div>}
    </form>
  </Form>
}

async function verificationResponseError(response: Response): Promise<string> {
  if (response.status !== 400 && response.status !== 503) {
    return "reCAPTCHA could not be verified. Please try again."
  }
  const body = await response.json().catch(() => undefined) as {error?: unknown} | undefined
  return typeof body?.error === "string"
    ? body.error
    : "reCAPTCHA could not be verified. Please try again."
}

async function responseError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => undefined) as {error?: unknown} | undefined
  return typeof body?.error === "string" ? body.error : fallback
}
